import type { Attributes, Context, Span } from '@opentelemetry/api';
import type { ReadableSpan, SpanProcessor } from '@opentelemetry/sdk-trace-base';

/** Identity of the navigation an HTTP request was issued under, minted by TelemetryService. */
export type NavigationId = number;

export interface NavigationSpanProcessorCallbacks {
  /** Returns the navigation that owns the starting request, or null when no navigation can own it. */
  onRequestStart: () => NavigationId | null;
  onRequestEnd: (owningNavigationId: NavigationId) => void;
}

const DEFAULT_TRACKED_URL_PATTERNS = ['/api/', '/proxy/'];

/**
 * Counts the in-flight HTTP spans of a navigation so TelemetryService can detect network idle.
 *
 * Trackedness is decided at span start, not at span end: a request counted on start but not
 * reported on end would strand its navigation at zero pending requests with no idle check armed,
 * leaving the navigation span open and therefore never exported.
 */
export class NavigationSpanProcessor implements SpanProcessor {
  #callbacks: NavigationSpanProcessorCallbacks | null;
  readonly #trackedUrlPatterns: string[];

  // Keyed by spanId because onStart is handed a Span and onEnd a ReadableSpan — different types
  // over the same underlying object.
  readonly #owningNavigationIdBySpanId = new Map<string, NavigationId>();

  constructor(
    callbacks: NavigationSpanProcessorCallbacks | null = null,
    trackedUrlPatterns: string[] = DEFAULT_TRACKED_URL_PATTERNS,
  ) {
    this.#callbacks = callbacks;
    this.#trackedUrlPatterns = trackedUrlPatterns;
  }

  /** Allows the processor to be handed to WebTracerProvider before Angular DI exists. */
  setCallbacks(callbacks: NavigationSpanProcessorCallbacks): void {
    this.#callbacks = callbacks;
  }

  onStart(span: Span, _parentContext: Context): void {
    const attributes = (span as unknown as ReadableSpan).attributes;
    if (!this.#isHttpSpan(attributes) || !this.#isTrackedUrl(attributes)) return;

    const owningNavigationId = this.#callbacks?.onRequestStart() ?? null;
    if (owningNavigationId === null) return;

    this.#owningNavigationIdBySpanId.set(span.spanContext().spanId, owningNavigationId);
  }

  onEnd(span: ReadableSpan): void {
    const spanId = span.spanContext().spanId;
    const owningNavigationId = this.#owningNavigationIdBySpanId.get(spanId);
    if (owningNavigationId === undefined) return;

    this.#owningNavigationIdBySpanId.delete(spanId);
    this.#callbacks?.onRequestEnd(owningNavigationId);
  }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }

  shutdown(): Promise<void> {
    this.#owningNavigationIdBySpanId.clear();
    return Promise.resolve();
  }

  /** `http.method` is the old semantic convention, `http.request.method` the current one. */
  #isHttpSpan(attributes: Attributes): boolean {
    return attributes['http.method'] !== undefined || attributes['http.request.method'] !== undefined;
  }

  #isTrackedUrl(attributes: Attributes): boolean {
    const url = attributes['http.url'] ?? attributes['url.full'];
    return typeof url === 'string' && this.#trackedUrlPatterns.some(pattern => url.includes(pattern));
  }
}
